import React, { lazy, useState } from 'react'
import { connect } from 'react-redux'
import styles from './RegionShowPage.scss'
import { StandardLayout } from 'common/layouts/StandardLayout'
import { ColWrap, MainCol, SecondaryCol } from 'common/layouts/StandardLayout/TwoCol'
import { CollapseStateContext, withCollapseState } from 'common/layouts/StandardLayout/StandardLayoutContexts'
import { SectionBox } from 'common/styleguide/SectionBox'
import { Card } from 'common/styleguide/Card'
import { CTA } from 'common/styleguide/CTA'
import { Loadable, LoadableCentered } from 'common/Loadable'
import { Icon } from 'common/Icon'
import { CircleSpinner } from 'common/spinners'
import { useFormatMessage, featureNames, commonNouns, commonCTAs, commonMessages } from 'lib/localization'
import { formatText } from 'lib/textUtils'
import { ambassadorRouteSelectors } from 'data/resources/ambassadorRoutes'
import { routeSelectors } from 'data/resources/routes'
import { tripSelectors } from 'data/resources/trips'
import { collectionSelectors } from 'data/resources/collections'
import { rideReportSelectors } from 'data/resources/rideReports'
import { clubSelectors } from 'data/resources/clubs'
import { regionActions, regionSelectors } from 'data/resources/regions'
import { routeSetOverviewSelectors } from 'data/resources/routeSetOverviews'
import { defineMessages } from 'react-intl'
import { withRouter, useParams } from 'react-router-dom'
import cx from 'classnames'
import {
  AmbassadorRouteListItem,
  RouteListItem,
  TripListItem,
  CollectionListItem,
  RideReportListItem,
  ClubListItem,
  SideColRegionListItem
} from '../RegionListItems'

// this is lazy because the normal form causes a module build error
const RegionMap = lazy(() => import('../RegionMap'))

const messages = defineMessages({
  changeLocation: {
    id: 'RegionShowPage.changeLocation',
    defaultMessage: 'Change Location'
  },
  likeWhatYouSee: {
    id: 'RegionShowPage.likeWhatYouSee',
    defaultMessage: 'Like What You See?'
  },
  rwgpsCreate: {
    id: 'RegionShowPage.rwgpsCreate',
    defaultMessage: 'Ride with GPS creates route planning and navigation tools across our website and app, enabling cyclists to go on better rides more often.'
  },
  findMore: {
    id: 'RegionShowPage.findMore',
    defaultMessage: 'Find more routes around {region}'
  },
  loadingMap: {
    id: 'RegionShowPage.loadingMap',
    defaultMessage: 'Loading your map'
  },
  nearbyRegions: {
    id: 'RegionShowPage.nearbyRegions',
    defaultMessage: 'Nearby Regions'
  },
  viewNearbyClubs: {
    id: 'RegionShowPage.viewNearbyClubs',
    defaultMessage: 'View Nearby Clubs'
  },
  viewNearbyRegions: {
    id: 'RegionShowPage.viewNearbyRegions',
    defaultMessage: 'View Nearby Regions'
  }
})

export const _RegionShowPage = ({
  currentUser,
  region,
  nearbyRegions,
  ambassadorRoutes,
  routes,
  trips,
  collections,
  rideReports,
  clubs,
  routeSetOverview,
  server
}) => {
  const fm = useFormatMessage()
  const [fullscreen, setFullscreen] = useState(false)

  const mapKey = `${region.id}-${fullscreen}`
  if (fullscreen) {
    return <>
      <Icon
        i='ios-close'
        size={50}
        className={styles.exitFullscreen}
        onClick={() => setFullscreen(false)}
      />
      <RegionMap
        key={mapKey}
        fullscreen
        routeSetOverview={routeSetOverview}
        updatedAt={region.updatedAt}
      />
    </>
  }

  return (
    <StandardLayout greyBg withCollapseProvider>
      <CollapseStateContext.Consumer>
        {(collapseState) => {
          return <>
            <div className={styles.banner}>
              <div
                style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${region.cover})` }}
                className={styles.bannerImg} />
              <div className={cx(styles.bannerText, collapseState && styles.collapsed)}>
                <div className={styles.topRides} data-heading-alt>{region.subtitle}</div>
                <h2>{region.shortDescription}</h2>
                <CTA margins={false} link to={`/regions/${region.zone}/${region.countryCode?.toLowerCase()}`}>{fm(messages.changeLocation)}</CTA>
              </div>
            </div>

            <ColWrap>
              <MainCol>
                {collapseState && <Overview region={region} fm={fm} collapseState={collapseState} />}

                <Card noPadding>
                  {server
                    ? <LoadableCentered
                      loading
                      loadingText={fm(messages.loadingMap)}
                    />
                    : <RegionMap
                      key={mapKey}
                      routeSetOverview={routeSetOverview}
                      updatedAt={region.updatedAt}
                      setFullscreen={setFullscreen}
                    />}
                </Card>

                <Card>
                  {server
                    ? <CircleSpinner centered size={40} />
                    : <>
                      {ambassadorRoutes.map(ar => <AmbassadorRouteListItem key={ar.id} ambassadorRoute={ar} />)}
                      {routes.map(r => <RouteListItem key={r.id} route={r} />)}
                      {trips.map(t => <TripListItem key={t.id} trip={t} />)}
                        </>}
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
                    <CTA secondary autoWidth margins={false} href={`/find#search/0/search%5Bstart_location%5D=${region.name}`}>{fm(messages.findMore, { region: region.name })}</CTA>
                  </div>
                </Card>

                {!currentUser?.id && <LikeWhatYouSee />}
              </MainCol>

              <SecondaryCol>
                {!collapseState && <Overview region={region} fm={fm} />}

                {collections.size > 0 && <SectionBox title={fm(featureNames.collections)}>
                  {collections.toIndexedSeq().map((c, idx) => <CollectionListItem key={c.id} collection={c} first={idx === 0} />)}
                </SectionBox>}

                {rideReports.size > 0 && <SectionBox title={fm(featureNames.rideReports)}>
                  {rideReports.map(rr => <RideReportListItem key={rr.id} rideReport={rr} />)}
                </SectionBox>}

                {clubs.size > 0 && <SectionBox title={fm(featureNames.clubs)}>
                  {clubs.map(c => <ClubListItem key={c.id} club={c} />)}
                  {/* TODO uncomment this once we have the ability to search for the other itemTypes */}
                  {/* <Link to={`/organizations/search?location=${region.name}`} className={styles.cardLink}>
                        {fm(messages.viewNearbyClubs)}
                      </Link> */}
                </SectionBox>}

                {nearbyRegions.length > 0 && <SectionBox title={fm(messages.nearbyRegions)}>
                  {nearbyRegions.map((r, idx) => <SideColRegionListItem
                    key={idx}
                    to={`/regions/${r.zone}/${r.countryCode.toLowerCase()}/${r.slug}`}
                    name={r.name}
                    idx={idx}
                  />)}
                </SectionBox>}
              </SecondaryCol>
            </ColWrap>
            </>
        }}
      </CollapseStateContext.Consumer>
    </StandardLayout>
  )
}

const Overview = ({ region, fm, collapseState }) => {
  const style = collapseState ? undefined : {
    backgroundImage: `url(${region.cover})`,
    height: '160px',
    marginBottom: '20px'
  }

  return <SectionBox title={collapseState ? undefined : fm(commonNouns.overview)}>
    <div
      className={cx(styles.bannerImg, collapseState && styles.collapsed)}
      style={style}
    />
    <div className={styles.regionDesc}>{formatText(region.description)}</div>
  </SectionBox>
}

const LikeWhatYouSee = withCollapseState(({ collapseState }) => {
  const fm = useFormatMessage()

  return <Card className={cx(styles.likeWhatYouSee, collapseState && styles.collapsed)}>
    <div className={styles.greyCircle}>
      <Icon size={80} i='logo-rwgps' />
    </div>

    <div className={styles.text}>
      <h3>
        {fm(messages.likeWhatYouSee)}
      </h3>

      <span>{fm(messages.rwgpsCreate)}</span>

      <a href='/features'>{fm(commonCTAs.learnMore)}</a>
    </div>
  </Card>
})

const loadInitialResources = ({ loadRegion }) => {
  return Promise.all([
    loadRegion()
  ])
}

const LoadableRegionShowPage = props => {
  const fm = useFormatMessage()
  const { slug } = useParams()
  return <Loadable
    {...props}
    resourceId={slug}
    component={_RegionShowPage}
    loadingText={`${fm(commonMessages.loading)}...`}
    resourceLoaded={() => loadInitialResources(props)}
  />
}

const mapStateToProps = (state, ownProps) => {
  const { slug } = ownProps.match.params
  const id = parseInt(slug.split('-')[0], 10)
  const itemIds = type => regionSelectors.itemIds(state, slug, type)

  return {
    currentUser: state.currentUser,
    region: regionSelectors.findBySlug(state, slug),
    nearbyRegions: regionSelectors.findByIds(state, regionSelectors.nearbyRegionIds(state, slug)),
    ambassadorRoutes: ambassadorRouteSelectors.findByIds(state, itemIds('ambassador_route')),
    routes: routeSelectors.findByIds(state, itemIds('route')),
    trips: tripSelectors.findByIds(state, itemIds('trip')),
    collections: collectionSelectors.findByIds(state, itemIds('collection')),
    rideReports: rideReportSelectors.findByIds(state, itemIds('ride_report')),
    clubs: clubSelectors.findByIds(state, itemIds('club')),
    routeSetOverview: routeSetOverviewSelectors.byParent(state, 'Region', id)
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const { slug } = ownProps.match.params
  return {
    loadRegion: () => dispatch(regionActions.read(slug))
  }
}

export const RegionShowPage = withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableRegionShowPage))
